import { morph, MORPH_OPTIONS } from '@theme/morph';

/**
 * A class to re-render sections using the Section Rendering API
 */
class SectionRenderer {
  /**
   * The cache of section HTML
   * @type {Map<string, string>}
   */
  #cache = new Map();

  /**
   * The abort controllers by section ID
   * @type {Map<string, AbortController>}
   */
  #abortControllersBySectionId = new Map();

  /**
   * The pending renders by section ID
   * @type {Map<string, { abortController: AbortController, promise: Promise<string> }>}
   */
  #pendingRendersBySectionId = new Map();

  /**
   * The pending promises
   * @type {Map<string, Promise<string>>}
   */
  #pendingPromises = new Map();

  /** Catalogue facet markup only; never retain a second grid of product images.
   * @type {Map<string, Promise<DocumentFragment>>}
   */
  #catalogueFilterOptions = new Map();

  constructor() {
    window.addEventListener('load', this.#cachePageSections.bind(this));
  }

  /**
   * Renders a section
   * @param {string} sectionId - The section ID
   * @param {Object} [options] - The options
   * @param {boolean} [options.cache] - Whether to use the cache
   * @param {'hydration'|'full'} [options.mode] - Which parts of the section to morph into the DOM
   * @param {boolean} [options.injectStylesheet=false] - When true, extracts
   * `style[data-section-stylesheet]` from the response and injects it into the section wrapper.
   * @param {URL} [options.url] - The URL to render the section from
   * @param {(html: string) => boolean} [options.shouldRender] - Return false to preserve the current section.
   * @returns {Promise<string>} The rendered section HTML
   */
  async renderSection(sectionId, options) {
    const { cache = !Shopify.designMode, mode = 'full', injectStylesheet = false } = options ?? {};
    const { url, shouldRender } = options ?? {};
    this.#abortPendingMorph(sectionId);

    const abortController = new AbortController();
    this.#abortControllersBySectionId.set(sectionId, abortController);

    const renderPromise = this.#renderSection(
      sectionId,
      { cache, mode, injectStylesheet, url, shouldRender },
      abortController
    );
    this.#pendingRendersBySectionId.set(sectionId, { abortController, promise: renderPromise });

    return renderPromise;
  }

  /**
   * Cancels a pending render for a section.
   * @param {string} sectionId - The section ID
   */
  abortRender(sectionId) {
    this.#abortPendingMorph(sectionId);
  }

  /**
   * Renders a section with an abort controller.
   * @param {string} sectionId - The section ID
   * @param {Object} options - The options
   * @param {boolean} options.cache - Whether to use the cache
   * @param {'hydration'|'full'} options.mode - Which parts of the section to morph into the DOM
   * @param {boolean} options.injectStylesheet - When true, injects stylesheet from the response
   * @param {URL} [options.url] - The URL to render the section from
   * @param {(html: string) => boolean} [options.shouldRender] - Return false to preserve the current section.
   * @param {AbortController} abortController - The abort controller for this render
   * @returns {Promise<string>} The rendered section HTML
   */
  async #renderSection(sectionId, { cache, mode, injectStylesheet, url, shouldRender }, abortController) {
    let sectionHTML = '';

    try {
      sectionHTML = await this.getSectionHTML(sectionId, cache, url, abortController.signal);
    } catch (error) {
      if (abortController.signal.aborted) {
        const pendingRender = this.#pendingRendersBySectionId.get(sectionId);
        if (pendingRender && pendingRender.abortController !== abortController) {
          return pendingRender.promise;
        }

        return sectionHTML;
      }

      throw error;
    } finally {
      if (this.#abortControllersBySectionId.get(sectionId) === abortController) {
        this.#abortControllersBySectionId.delete(sectionId);
      }

      const pendingRender = this.#pendingRendersBySectionId.get(sectionId);
      if (pendingRender?.abortController === abortController) {
        this.#pendingRendersBySectionId.delete(sectionId);
      }
    }

    if (!abortController.signal.aborted && (shouldRender?.(sectionHTML) ?? true)) {
      await morphSection(sectionId, sectionHTML, { mode, injectStylesheet });
    }

    return sectionHTML;
  }

  /**
   * Aborts an existing render for a section
   * @param {string} sectionId - The section ID
   */
  #abortPendingMorph(sectionId) {
    const existingAbortController = this.#abortControllersBySectionId.get(sectionId);
    if (existingAbortController) {
      existingAbortController.abort();
      this.#abortControllersBySectionId.delete(sectionId);
    }
  }

  /**
   * Gets the HTML for a section
   * @param {string} sectionId - The section ID
   * @param {boolean} useCache - Whether to use the cache
   * @param {URL} url - The URL to render the section for
   * @param {AbortSignal} [signal] - A signal that cancels the section render fetch
   * @returns {Promise<string>} The rendered section HTML
   */
  async getSectionHTML(sectionId, useCache = true, url = new URL(window.location.href), signal) {
    const sectionUrl = buildSectionRenderingURL(sectionId, url);

    const shouldSharePendingPromise = !signal;
    let pendingPromise = this.#pendingPromises.get(sectionUrl);
    if (pendingPromise) return pendingPromise;

    if (useCache) {
      const cachedHTML = this.#cache.get(sectionUrl);

      if (cachedHTML) return cachedHTML;
    }

    pendingPromise = fetch(sectionUrl, { signal }).then(async (response) => {
      if (!response.ok) throw new Error(`Failed to fetch section ${sectionId}: ${response.status}`);
      const html = await response.text();
      const requestURL = new URL(sectionUrl);
      if (!/\/collections\/best-selling-flavours\/?$/.test(requestURL.pathname) ||
          !html.includes('data-best-sellers-url=')) return html;

      // Shopify omits unavailable facets from the Best sellers response. The
      // initial Flavour Shop already has their order, so reuse only its forms.
      const catalogueURL = getFlavourShopURL(requestURL);
      const previewTheme = catalogueURL.searchParams.get('preview_theme_id');
      catalogueURL.search = '';
      if (previewTheme) catalogueURL.searchParams.set('preview_theme_id', previewTheme);
      try {
        const catalogueOptions = await this.#getCatalogueFilterOptions(sectionId, catalogueURL);
        return retainFlavourFilterOptions(html, catalogueOptions);
      } catch {
        // Product filtering remains usable if the option-list request fails.
        return retainFlavourFilterOptions(html, null);
      }
    });

    if (shouldSharePendingPromise) {
      pendingPromise = pendingPromise.finally(() => {
        this.#pendingPromises.delete(sectionUrl);
      });

      this.#pendingPromises.set(sectionUrl, pendingPromise);
    }

    const sectionHTML = await pendingPromise;

    this.#cache.set(sectionUrl, sectionHTML);
    return sectionHTML;
  }

  /**
   * Snapshot the complete option lists before the first Best sellers morph.
   * Direct entry on the native Best Selling collection needs one fallback
   * catalogue request; subsequent filters and pages share the same snapshot.
   * @param {string} sectionId
   * @param {URL} catalogueURL
   * @returns {Promise<DocumentFragment>}
   */
  #getCatalogueFilterOptions(sectionId, catalogueURL) {
    const key = buildSectionRenderingURL(sectionId, catalogueURL);
    const cached = this.#catalogueFilterOptions.get(key);
    if (cached) return cached;

    const section = document.getElementById(buildSectionSelector(normalizeSectionId(sectionId)));
    const forms = [...(section?.querySelectorAll('facets-form-component') ?? [])];
    let options;
    if (forms.length && forms.every(form =>
      form.getAttribute('data-collection-handle') === 'flavour-shop' ||
      form.getAttribute('data-wf-options-merged') === 'true'
    )) {
      options = Promise.resolve(parseSectionTemplate(forms.map(form => form.outerHTML).join('')).content);
    } else {
      options = this.getSectionHTML(sectionId, true, catalogueURL).then(html => {
        const template = parseSectionTemplate(html);
        // Discard the unrelated product grid, scripts and section styles.
        template.content.replaceChildren(...template.content.querySelectorAll('facets-form-component'));
        return template.content;
      });
    }
    const pending = options.catch(error => {
      this.#catalogueFilterOptions.delete(key);
      throw error;
    });
    this.#catalogueFilterOptions.set(key, pending);
    return pending;
  }

  /**
   * Caches the page sections
   */
  #cachePageSections() {
    for (const section of document.querySelectorAll('.shopify-section')) {
      const url = buildSectionRenderingURL(section.id);
      if (this.#cache.get(url)) continue;
      if (containsShadowRoot(section)) continue;
      const bestSellersForm = section.querySelector('facets-form-component[data-collection-handle="best-selling-flavours"]');
      if (bestSellersForm && !bestSellersForm.hasAttribute('data-wf-options-merged')) continue;
      // Shopify ignores our membership parameter on a full-page request.
      // Never cache that initial unfiltered grid as a Best sellers response.
      if (new URL(url).pathname !== window.location.pathname &&
          section.querySelector('facets-form-component')?.dataset.collectionHandle !== 'best-selling-flavours') continue;

      this.#cache.set(url, section.outerHTML);
    }
  }
}

const SECTION_ID_PREFIX = 'shopify-section-';

/**
 * Template contents have an inert owner document. DOMParser's detached HTML
 * documents can still download images before responsive sizes reach the page.
 * @param {string} html
 * @returns {HTMLTemplateElement}
 */
function parseSectionTemplate(html) {
  const template = document.createElement('template');
  template.innerHTML = html;
  return template;
}

/**
 * Retain the catalogue's option order while keeping Shopify's current counts
 * and selections. Only absent values are added as disabled, zero-count rows.
 * @param {string} html - Filtered section returned by Shopify
 * @param {DocumentFragment | null} catalogue - Unfiltered Flavour Shop forms
 * @returns {string}
 */
function retainFlavourFilterOptions(html, catalogue) {
  const template = parseSectionTemplate(html);
  const result = template.content;
  for (const form of result.querySelectorAll('facets-form-component')) {
    const formId = form.querySelector('form')?.id;
    const sourceForm = formId ? catalogue?.getElementById(formId)?.closest('facets-form-component') : null;
    form.setAttribute('data-wf-options-merged', sourceForm ? 'true' : 'false');
    if (!sourceForm) continue;
    const container = form.querySelector('.facets__filters-wrapper');
    if (!container) continue;
    const orderedGroups = [];
    for (const source of sourceForm.querySelectorAll('facet-inputs-component[data-filter-param]')) {
      let target = [...form.querySelectorAll('facet-inputs-component[data-filter-param]')]
        .find(group => group.getAttribute('data-filter-param') === source.getAttribute('data-filter-param'));
      const existed = !!target;
      if (!target) {
        const sourceGroup = source.closest('.facets__item') ?? source.closest('.facets__panel');
        if (!sourceGroup) continue;
        const group = sourceGroup.cloneNode(true);
        container.append(group);
        target = /** @type {Element} */ (group).querySelector('facet-inputs-component');
      }
      if (!target) continue;
      const sourceList = source.querySelector('ul');
      const targetList = target.querySelector('ul');
      if (!sourceList || !targetList) continue;
      const currentRows = existed ? [...targetList.children] : [];
      const orderedRows = [];
      for (const sourceRow of sourceList.children) {
        const sourceInput = sourceRow.querySelector('input');
        if (!sourceInput) continue;
        let row = currentRows.find(row => {
          const input = row.querySelector('input');
          return input?.name === sourceInput.name && input?.value === sourceInput.value;
        });
        if (!row) {
          row = /** @type {Element} */ (sourceRow.cloneNode(true));
          const input = /** @type {HTMLInputElement} */ (row.querySelector('input'));
          const isReset = input.type === 'radio' && input.value === '';
          input.disabled = !isReset;
          input.checked = isReset;
          input.toggleAttribute('checked', isReset);
          row.classList.toggle('facets__inputs-list-item--disabled', !isReset);
          row.querySelector('.checkbox')?.classList.toggle('checkbox--disabled', !isReset);
          for (const count of row.querySelectorAll('.checkbox__label-count, .facets__value-count')) {
            count.textContent = `(${isReset ? form.getAttribute('data-results-count') ?? '0' : '0'})`;
            count.removeAttribute('hidden');
            count.removeAttribute('data-count-url');
          }
        }
        // Native collection indices differ when values are absent. Match the
        // full-list IDs so labels remain unique and point to the right input.
        const input = row.querySelector('input');
        if (input) {
          for (const label of row.querySelectorAll('label[for]')) {
            if (label.getAttribute('for') === input.id) label.setAttribute('for', sourceInput.id);
          }
          input.id = sourceInput.id;
        }
        orderedRows.push(row);
      }
      targetList.replaceChildren(...orderedRows, ...currentRows.filter(row => !orderedRows.includes(row)));
      const group = target.closest('.facets__item') ?? target.closest('.facets__panel');
      if (group) orderedGroups.push(group);
    }
    container.append(...orderedGroups);
  }
  return template.innerHTML;
}

/**
 * Keep collection membership in the Flavour Shop filter URL, including links
 * generated by Shopify while rendering the manually maintained collection.
 * @param {URL} url
 * @returns {URL}
 */
export function getFlavourShopURL(url) {
  const result = new URL(url);
  if (result.origin === window.location.origin && /\/collections\/best-selling-flavours\/?$/.test(result.pathname)) {
    result.pathname = result.pathname.replace(/best-selling-flavours\/?$/, 'flavour-shop');
    result.searchParams.set('wf_best_sellers', '1');
  }
  return result;
}

/** The theme only advertises routes whose product tags exist in the catalogue. */
function catalogueProfileRoutes() {
  const config = document.querySelector('[data-wf-catalogue-profile-routes]');
  if (!config) return [];
  try { return JSON.parse(config.textContent || '[]'); }
  catch { return []; }
}

/** Expand a clean profile path for requests that need native metafield facets. */
export function getNativeCatalogueURL(url) {
  const result = new URL(url);
  if (result.origin !== window.location.origin) return result;
  const match = result.pathname.match(/^(.*\/collections\/flavour-shop)\/([^/]+)\/?$/);
  if (!match) return result;
  const route = catalogueProfileRoutes().find(route => route.slug === match[2]);
  if (!route) return result;
  const parameter = 'filter.p.m.custom.flavour_profiles';
  if (!result.searchParams.getAll(parameter).some(value => route.aliases.includes(value))) {
    result.searchParams.append(parameter, route.value);
  }
  result.pathname = match[1];
  return result;
}

/**
 * Use a real Shopify tag route for one known profile. Multiple profiles retain
 * native AND semantics. Form data already contains the checked path profile,
 * so an unchecked checkbox must remove that path instead of restoring it.
 */
export function getCleanCatalogueURL(url, { fromForm = false } = {}) {
  let result = new URL(url);
  if (result.origin !== window.location.origin) return result;
  const routes = catalogueProfileRoutes();
  const match = result.pathname.match(/^(.*\/collections\/flavour-shop)\/([^/]+)\/?$/);
  if (match && routes.some(route => route.slug === match[2])) {
    if (fromForm) result.pathname = match[1];
    else result = getNativeCatalogueURL(result);
  }
  if (!/\/collections\/flavour-shop\/?$/.test(result.pathname)) return result;
  // Best sellers is rendered through its own collection and needs native facets.
  if (result.searchParams.get('wf_best_sellers') === '1') return result;
  const parameter = 'filter.p.m.custom.flavour_profiles';
  const values = result.searchParams.getAll(parameter);
  if (values.length !== 1) return result;
  const route = routes.find(route => route.aliases.includes(values[0]));
  if (!route) return result;
  result.pathname = result.pathname.replace(/\/$/, '') + '/' + route.slug;
  result.searchParams.delete(parameter);
  return result;
}

/**
 * Builds a section rendering URL
 * @param {string} sectionId - The section ID
 * @param {URL} url - The URL to render the section for
 * @returns {string} The section rendering URL
 */
function buildSectionRenderingURL(sectionId, url = new URL(window.location.href)) {
  url = new URL(url);
  const section = document.getElementById(buildSectionSelector(normalizeSectionId(sectionId)));
  // Shopify handles the complete intersection and pagination on the server.
  // Only the background results request uses the Best Selling collection.
  if (section?.querySelector('results-list') && url.origin === window.location.origin &&
      /\/collections\/flavour-shop\/?$/.test(url.pathname) && url.searchParams.get('wf_best_sellers') === '1') {
    url.pathname = url.pathname.replace(/flavour-shop\/?$/, 'best-selling-flavours');
  }
  url.searchParams.delete('wf_best_sellers');
  url.searchParams.set('section_id', normalizeSectionId(sectionId));
  url.searchParams.sort();

  return url.toString();
}

/**
 * Builds a section selector
 * @param {string} sectionId - The section ID
 * @returns {string} The section selector
 */
export function buildSectionSelector(sectionId) {
  return `${SECTION_ID_PREFIX}${sectionId}`;
}

/**
 * Normalizes a section ID
 * @param {string} sectionId - The section ID
 * @returns {string} The normalized section ID
 */
export function normalizeSectionId(sectionId) {
  return sectionId.replace(new RegExp(`^${SECTION_ID_PREFIX}`), '');
}

/**
 * Checks if an element contains a shadow root
 * @param {Element} element - The element to check
 * @returns {boolean} Whether the element contains a shadow root
 */
function containsShadowRoot(element) {
  return !!element.shadowRoot || Array.from(element.children).some(containsShadowRoot);
}

/**
 * @typedef {(previousElement: HTMLElement, newElement: HTMLElement) => void} UpdateCallback
 */

/**
 * Morphs the existing section element with the new section contents
 *
 * @param {string} sectionId - The section ID
 * @param {string} html - The new markup the section should morph into
 * @param {Object} [options] - Additional options
 * @param {'hydration'|'full'} [options.mode] - Which parts of the section to morph into the DOM. 'hydration' will only morph nodes with `data-hydration-key` attributes.
 * @param {boolean} [options.injectStylesheet=false] - When true, extracts
 * `style[data-section-stylesheet]` from the response and injects it into the section wrapper.
 */
export async function morphSection(sectionId, html, options = {}) {
  const { mode = 'full', injectStylesheet = false } = options;
  const fragment = parseSectionTemplate(html).content;
  const existingElement = document.getElementById(buildSectionSelector(sectionId));
  const newElement = fragment.getElementById(buildSectionSelector(sectionId));

  if (!existingElement) {
    throw new Error(`Section ${sectionId} not found`);
  }

  if (!newElement) {
    throw new Error(`Section ${sectionId} not found in the section rendering response`);
  }

  if (newElement.querySelector('wf-best-sellers-filter')) {
    for (const link of newElement.querySelectorAll('.pagination a[href]')) {
      const url = getFlavourShopURL(new URL(link.getAttribute('href'), window.location.href));
      link.setAttribute('href', url.pathname + url.search + url.hash);
    }
  }

  morph(existingElement, newElement, {
    ...MORPH_OPTIONS,
    hydrationMode: mode === 'hydration',
  });

  if (injectStylesheet) {
    injectSectionStylesheet(fragment, existingElement);
  }
}

/**
 * Injects a `<style data-section-stylesheet>` from the parsed SFR response
 * into the live section wrapper. Replaces the existing stylesheet, if it exists.
 *
 * @param {DocumentFragment} fragment - The inert parsed response
 * @param {HTMLElement} sectionElement - The live section wrapper element
 */
function injectSectionStylesheet(fragment, sectionElement) {
  const newStylesheet = fragment.querySelector('style[data-section-stylesheet]');
  if (!newStylesheet) return;

  const existingStylesheet = sectionElement.querySelector('style[data-section-stylesheet]');

  if (existingStylesheet) {
    existingStylesheet.textContent = newStylesheet.textContent;
  } else {
    sectionElement.prepend(newStylesheet);
  }
}

export const sectionRenderer = new SectionRenderer();
